import React, { useRef, useState, useEffect } from "react";
import { Modal, Spin, Button, message, Input, List, Avatar, Table } from "antd";
import HomeRep from "../repository/homeRep";

export default function Testimony() {
  const uploader = useRef();
  const [testimony, setTestimony] = useState({
    name: "",
    title: "",
    quote: "",
    image: null, // { name, base64, type }
  });
  const [testimonies, setTestimonies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch testimonials on mount
  useEffect(() => {
    fetchTestimonies();
  }, []);

  const fetchTestimonies = async () => {
    setLoading(true);
    const response = await HomeRep.getTestimonies();
    if (response?.error) {
      message.error("Failed to fetch testimonials");
    } else {
      setTestimonies(response.data || []);
    }
    setLoading(false);
  };

  // Drag & drop handlers
  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await handleFile(file);
  };
  const handleDragOver = (e) => e.preventDefault();

  // File input handler
  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) await handleFile(file);
  };

  // File processing
  const handleFile = async (file) => {
    if (file.size > 5242880) {
      message.error("File too big! Max 5MB.");
      return;
    }
    setLoading(true);
    const base64 = await getBase64(file);
    setTestimony((prev) => ({
      ...prev,
      image: { name: file.name, base64, type: file.type },
    }));
    setLoading(false);
  };

  // Utility
  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleDelete = () => setTestimony((prev) => ({ ...prev, image: null }));

  const openModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    setTestimony({
      name: "",
      title: "",
      quote: "",
      image: null,
    });
  };

  const handleOk = async () => {
    const { name, title, quote, image } = testimony;
    if (!name || !title || !quote || !image?.base64) {
      message.error("Please fill all fields and upload an image.");
      return;
    }
    setLoading(true);

    // Prepare payload for backend
    const payload = {
      name,
      title,
      quote,
      image: image.base64,
    };

    const response = await HomeRep.addTestimony(payload);

    if (response?.error) {
      message.error(response.message || "Failed to update testimony");
    } else {
      setIsModalOpen(false);
      message.success("Testimony updated successfully");
      fetchTestimonies(); // Refresh list
    }
    setLoading(false);

    
  };

   const handleDeleteTestimony = async (id) => {
     setLoading(true);
     const response = await HomeRep.deleteTestimony(id);
     if (response?.error) {
       message.error("Failed to delete testimony");
     } else {
       message.success("Testimony deleted successfully");
       fetchTestimonies();
       setTimeout(() => {
         window.location.reload();
       }, 500);
     }
     setLoading(false);
   };

   const confirmDelete = (id) => {
     Modal.confirm({
       title: "Confirm Delete",
       content: "Are you sure you want to delete this testimony?",
       okText: "Delete",
       okType: "danger",
       cancelText: "Cancel",
       onOk() {
         handleDeleteTestimony(id);
       },
     });
   };

   const columns = [
     {
       title: "Image",
       dataIndex: "image",
       key: "image",
       render: (img) =>
         img ? (
           <Avatar src={img} size={48} />
         ) : (
           <Avatar icon="user" size={48} />
         ),
       width: 80,
     },
     {
       title: "Name",
       dataIndex: "name",
       key: "name",
       render: (text) => <strong>{text}</strong>,
       width: 180,
     },
     {
       title: "Title",
       dataIndex: "title",
       key: "title",
       render: (text) => <span style={{ color: "#888" }}>{text}</span>,
       width: 220,
     },
     {
       title: "Quote",
       dataIndex: "quote",
       key: "quote",
       render: (text) => (
         <span style={{ fontStyle: "italic", color: "#444" }}>{text}</span>
       ),
     },
     {
       title: "Actions",
       key: "actions",
       render: (_, record) => (
         <Button
           type="link"
           danger
           icon={<i className="fas fa-trash"></i>}
           onClick={() => confirmDelete(record._id)}
         />
       ),
       width: 100,
     },
   ];


  return (
    <section className="mt-md-3">
      <div className="page-header flex-md-row flex-column">
        <h1>Testimonials</h1>
        <Button type="primary" className="primary-btn" onClick={openModal}>
          Add Testimony
        </Button>
      </div>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={testimonies}
          rowKey="_id"
          pagination={false}
          style={{ marginTop: 32, background: "#fff" }}
        />
      </Spin>
      <Modal
        title="Add Testimony"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleOk}
            loading={loading}
            disabled={
              !testimony.name ||
              !testimony.title ||
              !testimony.quote ||
              !testimony.image?.base64
            }
          >
            Confirm
          </Button>,
        ]}
        className="custom-modal"
      >
        <Spin spinning={loading}>
          <Input
            placeholder="Name"
            value={testimony.name}
            onChange={(e) =>
              setTestimony((prev) => ({ ...prev, name: e.target.value }))
            }
            style={{ marginBottom: 12 }}
          />
          <Input
            placeholder="Title"
            value={testimony.title}
            onChange={(e) =>
              setTestimony((prev) => ({ ...prev, title: e.target.value }))
            }
            style={{ marginBottom: 12 }}
          />
          <Input.TextArea
            placeholder="Quote"
            value={testimony.quote}
            onChange={(e) =>
              setTestimony((prev) => ({ ...prev, quote: e.target.value }))
            }
            style={{ marginBottom: 12 }}
            rows={3}
          />
          <div
            className="custom-dragger p-0 w-100"
            style={{
              border: "2px dashed #aaa",
              borderRadius: 16,
              padding: 0,
              textAlign: "center",
              cursor: "pointer",
              minHeight: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              margin: "32px 0",
              background: "#fafbfc",
            }}
            onClick={() => uploader.current.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              accept="image/*"
              ref={uploader}
              onChange={onFileChange}
              className="d-none"
            />
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  color: "#aaa",
                  marginBottom: 12,
                  border: "1.5px solid #ddd",
                }}
              >
                <span style={{ fontSize: 36, fontWeight: "bold" }}>+</span>
              </div>
              <div>
                <p style={{ color: "#888", fontSize: 15, margin: 0 }}>
                  Click or drag & drop to upload image (max 5MB)
                </p>
              </div>
            </div>
          </div>
          {testimony.image && testimony.image.base64 && (
            <div style={{ width: "100%", textAlign: "center", marginTop: 16 }}>
              <img
                src={testimony.image.base64}
                alt="Preview"
                style={{
                  maxWidth: 200,
                  maxHeight: 200,
                  borderRadius: 8,
                  border: "1px solid #eee",
                }}
              />
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {testimony.image.name}
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </section>
  );
}
